import { defineStore } from 'pinia';
import axios from 'axios';
import { ref } from 'vue';
import { Constant } from '@/config/constants';

// Add list of public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
    `${Constant.PACKAGES}`,
    '/pricing',
    '/duck/',
    '/duck-found/',
    '/get-cruise-info-from-duck/',
    Constant.GET_DUCKS_AND_CRUISE_BY_UUID,
    Constant.GET_DUCK_BY_UUID
];

// Check if a URL endpoint is public
const isPublicEndpoint = (url: string): boolean => {
    // Always require authentication for any URL containing "/auth/"
    if (url.includes('/auth/')) {
        return false;
    }
    
    // Check against public endpoints list
    return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

export const useAuthStore = defineStore('auth', () => {
    const token = ref<string | null>(null);
    const requestInterceptorId = ref<number | null>(null);

    const initAuth = () => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            // Only set token if it's valid
            if (!isTokenExpired(storedToken)) {
                token.value = storedToken;
                // Set up axios interceptor for auth headers
                setupAxiosInterceptors();
            } else {
                // Clear invalid token
                localStorage.removeItem('token');
            }
        } else {
            // Ensure interceptors are set up even without token
            setupAxiosInterceptors();
        }
    };

    // Check if token is expired
    const isTokenExpired = (tokenValue: string): boolean => {
        try {
            const payload = tokenValue.split('.')[1];
            if (!payload) return true;
            
            const decoded = JSON.parse(atob(payload));
            // Check if token is expired
            return decoded.exp * 1000 < Date.now();
        } catch (e) {
            console.error('Error decoding token:', e);
            return true;
        }
    };

    // Check if token is valid
    const isTokenValid = (): boolean => {
        if (!token.value) return false;
        return !isTokenExpired(token.value);
    };
    
    const login = async (credentials: { email: string; password: string; }): Promise<boolean> => {
        try {
            // Clear any previous token
            token.value = null;
            localStorage.removeItem('token');
            
            // Use Promise.race to set a fast timeout for login response
            const loginResponse = await Promise.race([
                axios.post(Constant.API_URL + Constant.LOGIN, credentials),
                // Add a 5-second timeout to fail fast
                new Promise<never>((_, reject) => 
                    setTimeout(() => reject(new Error('Login request timeout')), 5000)
                )
            ]);
            
            if (loginResponse.data.token) {
                // Set token immediately after successful login
                token.value = loginResponse.data.token;
                localStorage.setItem('token', loginResponse.data.token);
                
                // Return true immediately to allow for faster UI updates
                return true;
            }
            return false;
        } catch (error) {
            // Clear token in case of error
            token.value = null;
            localStorage.removeItem('token');
            throw error;
        }
    };

    // Add a new method that handles errors without throwing
    const loginWithErrorHandling = async (credentials: { email: string; password: string }) => {
        try {
            // Clear any previous token
            token.value = null;
            localStorage.removeItem('token');
            
            // Wait for login response
            const loginResponse = await axios.post(Constant.API_URL + Constant.LOGIN, credentials);
            
            if (loginResponse.data.token) {
                // Set token after successful login
                token.value = loginResponse.data.token;
                localStorage.setItem('token', loginResponse.data.token);
                
                // Return success
                return { success: true };
            }
            
            // No token in response
            return { 
                success: false, 
                status: loginResponse.status,
                message: 'Login failed: No authentication token received' 
            };
        } catch (error: any) {
            // Clear token in case of error
            token.value = null;
            localStorage.removeItem('token');
            
            // Return error information but don't throw
            return { 
                success: false, 
                status: error.response?.status,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    };

    const register = async (credentials: { email: string; password: string; redirectUrl?: string }) => {
        await axios.post(Constant.API_URL + Constant.REGISTER, credentials);
    };

    // Add a new method that handles registration errors without throwing
    const registerWithErrorHandling = async (credentials: { email: string; username?: string; password: string; redirectUrl?: string }) => {
        try {
            const response = await axios.post(Constant.API_URL + Constant.REGISTER, credentials);
            
            // Return success
            return { success: true, data: response.data };
        } catch (error: any) {
            // Return error information but don't throw
            return { 
                success: false, 
                status: error.response?.status,
                message: error.response?.data?.message || 'Registration failed',
                code: error.response?.data?.code
            };
        }
    };

    const confirmEmail = async (confirmToken: string, redirectUrl?: string) => {
        const params: any = { token: confirmToken };
        if (redirectUrl) {
            params.redirectUrl = redirectUrl;
        }
        
        const response = await axios.get(Constant.API_URL + Constant.CONFIRM_EMAIL, {
            params
        });
        
        // If we get a login token in the response, automatically log the user in
        if (response.data.loginToken) {
            token.value = response.data.loginToken;
            localStorage.setItem('token', response.data.loginToken);
        }
        
        return response.data;
    };

    const logout = () => {
        // Clear all auth data
        token.value = null;
        
        // Remove from local storage
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        
        // Clear any other auth related data
        sessionStorage.removeItem('authRedirect');
        
        // If using cookies, clear them too
        document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        
        // Navigation should be handled by the component that called logout
    };

    // Updated to check token validity
    const isAuthenticated = () => {
        return !!token.value && isTokenValid();
    };

    // Setup axios interceptor to handle auth headers
    const setupAxiosInterceptors = () => {
        // Remove any existing interceptors if they exist
        if (requestInterceptorId.value !== null) {
            axios.interceptors.request.eject(requestInterceptorId.value);
            requestInterceptorId.value = null;
        }
        
        // Add request interceptor and store its ID
        requestInterceptorId.value = axios.interceptors.request.use(
            (config) => {
                // Skip adding auth token for public endpoints
                if (config.url && isPublicEndpoint(config.url)) {
                    return config;
                }
                
                // Only add valid token to requests
                const currentToken = token.value || localStorage.getItem('token');
                if (currentToken && !isTokenExpired(currentToken)) {
                    config.headers.Authorization = `Bearer ${currentToken}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    };

    // Call setup interceptors when store is created
    setupAxiosInterceptors();

    return {
        token,
        login,
        loginWithErrorHandling,
        register,
        registerWithErrorHandling,
        confirmEmail,
        logout,
        isAuthenticated,
        isTokenValid,
        initAuth
    };
});
